/**
 * Client-safe shared internals for the community action modules.
 *
 * This module is NOT a `'use server'` module and carries NO server-only
 * transitive imports — it holds only the pure `normalizeHashtag` helper and
 * the Zod schemas reused across the post / comment / follow / organization /
 * community-event / post-tag / mention action groups.
 *
 * It MUST stay client-safe: the public barrel `./actions.ts` re-exports
 * `normalizeHashtag` from here, so a `'use client'` component importing the
 * barrel pulls this module's full graph into the browser bundle. Anything
 * that touches the Supabase server / admin clients or the notification
 * handler lives in the server-only `./community-server-internals.ts` instead
 * (imported directly by the `'use server'` leaf modules, which Next stubs as
 * RPC refs on the client).
 */

import { z } from 'zod';

/**
 * Normalize a raw hashtag URL segment to the canonical tag: lowercased, and
 * stripped to letters / marks / digits / underscore (matching the `#tag`
 * linkifier in `post-body.tsx`). Returns null for an empty result so callers
 * can short-circuit to an empty feed instead of running a wildcard query.
 *
 * Lives here (not in a `'use server'` module) so both the hashtag read action
 * and the server-component page can import this sync helper.
 */
export function normalizeHashtag(raw: string): string | null {
  const cleaned = raw
    .toLowerCase()
    .replace(/^#/, '')
    .replace(/[^\p{L}\p{M}0-9_]/gu, '');
  return cleaned.length > 0 ? cleaned : null;
}

// =====================
// Zod Schemas
// =====================
export const ShareTypeSchema = z.enum([
  'copy_link',
  'twitter',
  'facebook',
  'linkedin',
  'whatsapp',
  'email',
]);

export const PostIdSchema = z.guid();
export const CommentIdSchema = z.guid();
export const ProfileIdSchema = z.guid();
export const OrganizationIdSchema = z.guid();
export const TagIdSchema = z.guid();
